import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { Avatar } from '../common/Avatar';
import NotificationDropdown from '../common/NotificationDropdown';
import { useTheme } from '../../contexts/ThemeContext';
import './MainLayout.css';

const MainLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { theme, changeTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canViewUsers = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canViewAudit = ['super_admin', 'admin'].includes(user?.role);
  // Workflow management restricted to Admin and Super Admin only
  const canViewWorkflows = ['super_admin', 'admin'].includes(user?.role);

  return (
    <div className="main-layout">
      {/* Top Header */}
      <header className="top-header">
        <div className="header-left">
          <button
            className="menu-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
          <div className="header-brand">
            <img src="/logo-icon.png" alt="Workstack" className="header-logo" />
            <span className="header-app-name">Workstack</span>
          </div>
        </div>
        <div className="header-right">
          <NotificationDropdown />
          <div className="header-user-dropdown" ref={userMenuRef}>
            <button
              className="header-user-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <Avatar user={user} size="sm" />
              <span className="header-user-name">{user?.first_name} {user?.last_name}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M6 8L2 4h8z" />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="user-dropdown-menu">
                <div className="dropdown-user-info">
                  <Avatar user={user} size="md" />
                  <div className="dropdown-user-text">
                    <div className="dropdown-user-name">{user?.first_name} {user?.last_name}</div>
                    <div className="dropdown-user-email">{user?.email}</div>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <button
                  className="dropdown-item"
                  onClick={() => {
                    navigate('/profile');
                    setUserMenuOpen(false);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <span>My Profile</span>
                </button>

                <div className="dropdown-divider" />

                <div className="dropdown-theme-section">
                  <div className="dropdown-section-label">Theme</div>
                  <div className="dropdown-theme-buttons">
                    <button
                      className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                      onClick={() => changeTheme('light')}
                    >
                      ☀️
                    </button>
                    <button
                      className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                      onClick={() => changeTheme('dark')}
                    >
                      🌙
                    </button>
                    <button
                      className={`theme-btn ${theme === 'system' ? 'active' : ''}`}
                      onClick={() => changeTheme('system')}
                    >
                      💻
                    </button>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <button
                  className="dropdown-item dropdown-item-danger"
                  onClick={() => {
                    handleLogout();
                    setUserMenuOpen(false);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            {!sidebarCollapsed && <div className="sidebar-section-label">Main</div>}
            <NavLink to="/dashboard" className="nav-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              {!sidebarCollapsed && <span>Dashboard</span>}
            </NavLink>

            <NavLink to="/projects" className="nav-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              {!sidebarCollapsed && <span>Projects</span>}
            </NavLink>

            <NavLink to="/library" className="nav-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 5a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H2V5z" />
                <path fillRule="evenodd" d="M2 9h16v5a2 2 0 01-2 2H4a2 2 0 01-2-2V9zm4 2a1 1 0 000 2h4a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              {!sidebarCollapsed && <span>Library</span>}
            </NavLink>

            <NavLink to="/reporting" className="nav-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 4a1 1 0 011-1h4v2H3V4zm0 4h6v2H2V8zm0 4h9v2H2v-2z"/>
                <path d="M16 3a1 1 0 011 1v12a1 1 0 01-1 1h-4a1 1 0 01-1-1V4a1 1 0 011-1h4z"/>
              </svg>
              {!sidebarCollapsed && <span>Reporting</span>}
            </NavLink>
          </div>
          {canViewUsers && <div className="sidebar-divider" />}
          {canViewUsers && <div className="sidebar-section">
            {!sidebarCollapsed && <div className="sidebar-section-label">Admin</div>}
            {canViewUsers && (
              <NavLink to="/users" className="nav-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                {!sidebarCollapsed && <span>Users</span>}
              </NavLink>
            )}

            {canViewWorkflows && (
              <NavLink to="/workflows" className="nav-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                {!sidebarCollapsed && <span>Workflows</span>}
              </NavLink>
            )}

            {canViewAudit && (
              <NavLink to="/audit" className="nav-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4-1a1 1 0 10-2 0v7a1 1 0 102 0V8z" clipRule="evenodd" />
                </svg>
                {!sidebarCollapsed && <span>Audit Logs</span>}
              </NavLink>
            )}


            {canViewWorkflows && (
              <NavLink to="/task-types" className="nav-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                </svg>
                {!sidebarCollapsed && <span>Task Types</span>}
              </NavLink>
            )}

          </div>}
        </nav>

        <div className="sidebar-footer">
          <button
            className="user-menu"
            onClick={() => navigate('/profile')}
            title="Go to Profile"
          >
            <Avatar user={user} size="sm" />
            {!sidebarCollapsed && (
              <div className="user-info">
                <span className="user-name">{user?.first_name} {user?.last_name}</span>
                <span className="user-role">{user?.role?.replace('_', ' ')}</span>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default MainLayout;
